import { ReactNode } from "react";
import Box from "@mui/material/Box";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

/**
 * SIMS-8594 — the card presentation of the Pattern v2 config list.
 *
 * The second of two interchangeable renderers beneath the shell. Same records and same state as
 * the table; only the drawing differs (AC1). Stateless, like `ConfigTable`, so switching views
 * cannot lose the toolbar state (TestRail C17595).
 *
 * Spec — `Payers Pattern v2.dc.html`:
 *   grid  L285: repeat(3, 1fr), gap 16px
 *   card  L287: --surface-0, 1px --border-default, radius 12px, --shadow-1, padding 16px,
 *               column flex, gap 12px
 *   icon  L289: 40x40, radius 10px, 22px glyph, --surface-2 background, --icons-main colour
 *   title L291: 16px / 600 / line-height 20px
 *   sub   L292: monospace 11px --text-secondary, nowrap + ellipsis (the member-ID pattern)
 *
 * DELIBERATE DEVIATION: the prototype hangs the open handler on a plain <div> (L290). A div is
 * not focusable, and §6 requires every interactive element to be keyboard operable in DOM
 * order, so the title block is a real <button>. Visually identical; reachable by keyboard.
 *
 * The design's 3-up grid is fixed at the 1440 desktop viewport it targets (NFR-1). It is
 * expressed here as auto-fill with a floor so a narrower window degrades rather than clipping —
 * tablet layouts are explicitly out of scope, not explicitly forbidden.
 */

export interface CardStat {
	label: string;
	value: ReactNode;
	/** Render the value monospaced — the ID pattern does (L344). */
	mono?: boolean;
}

export interface ConfigCardsProps<T> {
	rows: T[];
	rowKey: (row: T) => string;
	title: (row: T) => ReactNode;
	/** Secondary line — the member-ID pattern for payers. Rendered monospace, ellipsised. */
	subtitle?: (row: T) => ReactNode;
	/**
	 * Whether the subtitle is monospaced. True by default, because the line it was built for
	 * is an identifier — a payer's member-ID pattern, an action's code — and a proportional
	 * face is measurably worse at comparing those character by character.
	 *
	 * A Groups card opts out: its subtitle is "Position 3", prose rather than an identifier,
	 * and the design sets it in the plain 11px secondary face (`Actions Pattern v2.dc.html:550`
	 * against `:342` for the action card, which is where the two diverge).
	 */
	subtitleMono?: boolean;
	/** Show the leading icon tile with the shell's default glyph — the config screen's own noun. */
	showIcon?: boolean;
	/**
	 * A glyph for the leading tile, chosen PER ROW — the Actions cards draw the group's own
	 * icon there (SIMS-9175), the same one the group carries in the table's Group column and
	 * on the Groups tab.
	 *
	 * Return null for a row that has no glyph and the tile is not drawn at all, rather than
	 * drawn empty: an action with no group is a permanent, legitimate state (FR-12b) and the
	 * table already treats it this way, giving Ungrouped no icon because it is a real value
	 * but not a configured section. Takes precedence over `showIcon`.
	 */
	icon?: (row: T) => ReactNode;
	/** Trailing slot: the status pill and the row kebab. */
	trailing?: (row: T) => ReactNode;
	/**
	 * Description line (design L333): 13px/18, `min-height: 36px` so a record without one does
	 * not make its card shorter than its neighbours in the 3-up grid.
	 */
	description?: (row: T) => ReactNode;
	/**
	 * The stat strip (design L334-346): up to three value/label pairs, bordered top and bottom.
	 * Omit for a screen with nothing to put there.
	 */
	stats?: (row: T) => CardStat[];
	/** Anything further below the strip — e.g. the category chips (SIMS-8634). */
	body?: (row: T) => ReactNode;
	onRowClick?: (row: T) => void;
}

function ConfigCards<T>({
	rows,
	rowKey,
	title,
	subtitle,
	subtitleMono = true,
	showIcon = false,
	icon,
	trailing,
	description,
	stats,
	body,
	onRowClick,
}: ConfigCardsProps<T>) {
	return (
		<Box
			// The three-column layout is only observable in a real browser — jsdom has no layout,
			// so an E2E check reads the computed grid here (SIMS-8594 AC1).
			data-testid="config-cards-grid"
			sx={{
				display: "grid",
				// EXACTLY three columns (design L307: `repeat(3, 1fr)`).
				//
				// This was `repeat(auto-fill, minmax(17.5rem, 1fr))` on the reasoning that a 17.5rem
				// floor "holds three columns at 1440" — it does not. auto-fill packs in as many
				// tracks as fit, so a wide window produced four or five NARROW cards instead of
				// three wide ones, which is why the cards read as too small.
				//
				// Fixed count, with breakpoint steps rather than auto-fill so narrow windows still
				// reflow: the design specifies the three-up layout and nothing below it.
				gridTemplateColumns: {
					xs: "1fr",
					sm: "repeat(2, 1fr)",
					md: "repeat(3, 1fr)",
				},
				gap: 2, // 16px
			}}
		>
			{rows.map((row) => {
				// One tile, one decision about what goes in it: a per-row glyph if the screen
				// supplies one, else the shell's default when the screen asked for the tile at
				// all. Null means no tile — not an empty one.
				const glyph = icon ? (
					icon(row)
				) : showIcon ? (
					<AccountBalanceIcon sx={{ fontSize: "1.375rem" }} />
				) : null;
				return (
					<Box
						key={rowKey(row)}
						data-testid="config-card"
						sx={{
							backgroundColor: "background.paper",
							border: "1px solid",
							borderColor: "divider",
							borderRadius: "0.75rem", // 12px
							boxShadow: 1,
							padding: 2, // 16px
							display: "flex",
							flexDirection: "column",
							gap: 1.5, // 12px
						}}
					>
						<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
							{glyph && (
								<Box
									data-testid="config-card-icon"
									sx={{
										flex: "0 0 2.5rem",
										width: "2.5rem",
										height: "2.5rem",
										borderRadius: "0.625rem", // 10px
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										// Design L243: the icon tile sits on --surface-2 = the `surfaceTwo` role.
										backgroundColor: (theme) => theme.palette.surfaceTwo.main,
										/*
										 * `--icons-main`, per L289 above — a brand mid-tone, and `primary.main`
										 * is the closest role that holds one. This read `primary.light`, which
										 * is not a brand purple at all: it is derived as a desaturated l=72
										 * tint (`colorUtils.ts:405-412`) for inactive icons on the DARK
										 * sidebar, so on a white card it came out a pale lilac.
										 *
										 * The Actions table already resolved the same token to `primary.main`
										 * for the group glyph in its Group column, and that glyph is now drawn
										 * on the cards too — the same icon in two views of one screen, which
										 * would have been two different purples had this stayed.
										 */
										color: "primary.main",
										"& svg": { fontSize: "1.375rem" },
									}}
								>
									{glyph}
								</Box>
							)}

							<Box
								// A real button, not the prototype's div — keyboard operability (§6).
								component={onRowClick ? "button" : "div"}
								type={onRowClick ? "button" : undefined}
								onClick={onRowClick ? () => onRowClick(row) : undefined}
								sx={{
									flex: 1,
									minWidth: 0,
									textAlign: "left",
									border: "none",
									background: "none",
									padding: 0,
									font: "inherit",
									color: "inherit",
									cursor: onRowClick ? "pointer" : "default",
								}}
							>
								<Box
									sx={{
										fontSize: "1rem",
										fontWeight: 600,
										lineHeight: "1.25rem",
									}}
								>
									{title(row)}
								</Box>
								{subtitle && (
									<Box
										sx={{
											...(subtitleMono && {
												fontFamily: "'SF Mono', Menlo, monospace",
											}),
											fontSize: "0.6875rem", // 11px
											color: "text.secondary",
											marginTop: "0.125rem",
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
										}}
									>
										{subtitle(row)}
									</Box>
								)}
							</Box>

							{trailing?.(row)}
						</Box>

						{/* Description (L333). Always rendered when the prop is supplied, with a
					    reserved min-height: an empty one must not shorten the card. */}
						{description && (
							<Box
								data-testid="config-card-description"
								sx={{
									fontSize: "0.8125rem",
									lineHeight: "1.125rem",
									color: "text.secondary",
									minHeight: "2.25rem",
									textWrap: "pretty",
								}}
							>
								{description(row)}
							</Box>
						)}

						{/* Stat strip (L334-346): bordered top AND bottom, 20px gaps, 10px 0 padding,
					    18px/700 values over 11px labels. */}
						{stats && stats(row).length > 0 && (
							<Box
								data-testid="config-card-stats"
								sx={{
									display: "flex",
									gap: 2.5,
									py: 1.25,
									borderTop: "1px solid",
									borderBottom: "1px solid",
									borderColor: "divider",
								}}
							>
								{stats(row).map((stat) => (
									<Box key={stat.label} sx={{ minWidth: 0 }}>
										<Box
											sx={{
												fontSize: stat.mono ? "0.8125rem" : "1.125rem",
												fontWeight: stat.mono ? 600 : 700,
												lineHeight: "1.375rem",
												...(stat.mono && {
													fontFamily: "'SF Mono', Menlo, monospace",
													whiteSpace: "nowrap",
													overflow: "hidden",
													textOverflow: "ellipsis",
												}),
											}}
										>
											{stat.value}
										</Box>
										<Box
											sx={{ fontSize: "0.6875rem", color: "text.secondary" }}
										>
											{stat.label}
										</Box>
									</Box>
								))}
							</Box>
						)}

						{body?.(row)}
					</Box>
				);
			})}
		</Box>
	);
}

export default ConfigCards;
